import { ContentCopy } from '@mui/icons-material';
import { Button } from '@mui/material';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { useAlertsStore } from '@/configs/store/useRootStore';
import { ITaskInfo } from '@/entities/task/model/client';
import { useYaMetrika } from '@/entities/yaMetrika';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { colorStore } from '@/features/queueColors';
import { MuiColors } from '@/types/colors';
import { AnalyticsEvent } from '@/types/yametrika';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { getTrackerUrl } from '@/utils/getTrackerUrl';
import { isMac } from '@/utils/isMac';

import s from './TaskItem.module.scss';

type TaskItemProps = {
  task: ITaskInfo;
  onOpenDetails?: (task: ITaskInfo) => void;
};

export const TaskItem: React.FC<TaskItemProps> = observer(({ task, onOpenDetails }) => {
  const alertsStore = useAlertsStore();
  const { sendEvent } = useYaMetrika();
  const todayDate = DateTime.local();
  const keyPrefix = task.key.split('-')[0];

  const color = colorStore.getColorByPrefix(keyPrefix);
  const needsBorder = colorStore.needsBorder(keyPrefix);

  const handleCopyLink = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const response = await copyToClipboard(getTrackerUrl(task.key));

    if (!response.isError) {
      alertsStore.addAlert({
        title: 'Ссылка успешно скопирована!',
        message: '',
        withReload: false,
        autoClose: true,
      });

      sendEvent(AnalyticsEvent.copyTaskName);
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const isContextMenuClick = isMac ? e.metaKey : e.ctrlKey;

    if (isContextMenuClick) {
      e.preventDefault();
      onOpenDetails?.(task);

      return;
    }

    modalManagerStore.open('addLog', {
      date: todayDate,
      hasPreselectedDate: false,
      taskKey: task.key,
      taskTitle: task.summary,
      isTaskSelected: true,
      isFromFavorites: true,
    });
  };

  const handleTaskLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    sendEvent(AnalyticsEvent.goToTask);
  };

  return (
    <div
      className={s['task-item']}
      onClick={handleCardClick}
      onContextMenu={(e) => {
        e.preventDefault();
        onOpenDetails?.(task);
      }}
    >
      <div
        className={s['task-item__color']}
        style={{
          backgroundColor: color,
          ...(needsBorder && {
            border: '1px solid rgba(0, 0, 0, 0.2)',
            borderLeft: 'none',
          }),
        }}
      ></div>
      <div className={s['task-item__content']}>
        <div className={s['task-item__header']}>
          <div>
            <a
              onClick={handleTaskLinkClick}
              href={getTrackerUrl(task.key)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color,
                ...(needsBorder && {
                  textShadow: '0 0 1px rgba(0, 0, 0, 0.5), 0 0 1px rgba(0, 0, 0, 0.5)',
                }),
              }}
            >
              {task.key}
            </a>
            <Button
              onClick={handleCopyLink}
              sx={{ padding: 0, minWidth: '5px', marginLeft: '5px', marginBottom: '3px' }}
            >
              <ContentCopy
                sx={{ width: '15px', height: '15px', color: MuiColors.secondaryColor }}
              />
            </Button>
          </div>
          <p className={s['task-item__title']}>{task.summary}</p>
        </div>
      </div>
    </div>
  );
});
